import React, { useState } from 'react';
import { GridCellProps } from '@progress/kendo-react-grid';
import { FileModel } from 'Models';
import { useToasts } from 'Context';
import { buildEndpoint, buildQueryUrl, useApiWorker } from 'Utilities';
import download from 'downloadjs';
import { ReportTypeField } from 'Models/AppointmentReportModel';

export type FileDownloadCellProps = {
  fileControllerEndpoint: string;
  optionalParams?: any;
  id?: string;
  filename?: string;
  display?: any;
  endpointActionName?: string;
  className?: string;
  noLoadingLabel?: boolean;
};

export const FileDownloadCell: React.FC<
  GridCellProps & FileDownloadCellProps
> = ({ dataItem, id, filename, ...downloadFileTextProps }) => {
  const rowModel = dataItem as FileModel;
  const rowModelTruthy = !!rowModel;

  const fileName = rowModelTruthy ? rowModel.fileName : filename || '';
  const entityId = rowModelTruthy ? rowModel.id : id || '';

  return (
    <td className="white-space-normal">
      <DownloadFileText
        id={entityId}
        filename={fileName}
        {...downloadFileTextProps}
      />
    </td>
  );
};

//Component to handle the download files depending the ReportTypeField
export const ReportsFilesDownloadCell: React.FC<
  GridCellProps & FileDownloadCellProps
> = ({ dataItem, id, filename, ...downloadFileTextProps }) => {
  const rowModel =
    downloadFileTextProps.field == ReportTypeField.TemplateField
      ? (dataItem.templateFile as FileModel)
      : (dataItem.reportFile as FileModel);

  const rowModelTruthy = !!rowModel;

  const fileName = rowModelTruthy ? rowModel.fileName : filename || '';
  const entityId = rowModelTruthy ? rowModel.id : id || '';

  return (
    <td className="white-space-normal">
      {entityId != null && (
        <DownloadFileText
          id={entityId}
          filename={fileName}
          {...downloadFileTextProps}
        />
      )}
      {entityId == null && <h4></h4>}
    </td>
  );
};

export const ReportsFilesDownloadIcon: React.FC<
  GridCellProps & FileDownloadCellProps
> = ({ dataItem, id, filename, ...downloadFileTextProps }) => {
  const rowModel =
    downloadFileTextProps.field == ReportTypeField.TemplateField
      ? (dataItem.templateFile as FileModel)
      : (dataItem.reportFile as FileModel);

  const rowModelTruthy = !!rowModel;

  const fileName = rowModelTruthy ? rowModel.fileName : filename || '';
  const entityId = rowModelTruthy ? rowModel.id : id || '';

  return (
    <>
      {entityId != null && (
        <DownloadFileText
          id={entityId}
          filename={fileName}
          {...downloadFileTextProps}
        />
      )}
      {entityId == null && <h4></h4>}
    </>
  );
};

function extractFilenameFromHeader(contentDisposition?: string): string | undefined {
  if (!contentDisposition) return undefined;
  const match = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : undefined;
}

export const DownloadFileText: React.FC<FileDownloadCellProps> = ({
  id,
  optionalParams,
  fileControllerEndpoint,
  endpointActionName = 'download',
  filename,
  display,
  className,
  children,
  noLoadingLabel,
}) => {
  const toast = useToasts();
  const API = useApiWorker();
  const [downloading, setDownloading] = useState(false); // Add state

  const downloadEndpoint = buildEndpoint(
    fileControllerEndpoint,
    endpointActionName
  );

  const downloadOrOpenFile = async () => {
    setDownloading(true);
    try {
      const { data, headers } = await API.get(
        buildQueryUrl(downloadEndpoint, { id, ...optionalParams }),
        {
          responseType: 'blob',
        }
      );

      const mimeType = headers['content-type'];
      const resolvedFilename = filename || extractFilenameFromHeader(headers['content-disposition']) || '';

      if (mimeType === 'application/pdf') {
        var fileURL = URL.createObjectURL(data);
        window.open(fileURL, resolvedFilename);
      } else {
        download(data, resolvedFilename, mimeType);
      }
    } catch (err) {
      toast.error('Error: Could not download file');
      console.log(err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <span
      className="column-download-icon"
      onClick={downloadOrOpenFile}
      style={{ cursor: downloading ? 'wait' : 'pointer' }}
    >
      {downloading && (
        <span className="download-spinner" style={{ marginRight: 8 }}>
          {/* Simple CSS spinner */}
          <svg width="18" height="18" viewBox="0 0 50 50">
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="#1976d2"
              strokeWidth="5"
              strokeDasharray="90,150"
              strokeDashoffset="0"
              strokeLinecap="round"
            >
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 25 25"
                to="360 25 25"
                dur="1s"
                repeatCount="indefinite"
              />
            </circle>
          </svg>
        </span>
      )}
      {downloading && !noLoadingLabel
        ? 'Downloading...'
        : downloading && noLoadingLabel
        ? null
        : children || display || filename}
    </span>
  );
};
